import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { FetchBaseQueryError, skipToken } from '@reduxjs/toolkit/query/react';
import { ProblemCode, Task, UpdateTaskRequest } from '@quizdb/common';
import { problemCode } from '../../api/problemCode';
import { useListTagsQuery } from '../../tag/tagEndpoints';
import { Icon } from '../../common/Icon';
import { LanguageCode, RelativeTime, TaskStatusPill } from '../TaskBadges';
import { TaskForm } from '../TaskForm';
import { TaskJobLink } from '../TaskJobLink';
import { TaskOutcomeNotice } from '../TaskOutcomeNotice';
import { TaskView } from '../TaskView';
import {
  TaskListFilters,
  useChangeTaskStatusMutation,
  useGetTaskQuery,
  useLazyListTasksFirstPageQuery,
  useUpdateTaskMutation,
} from '../taskEndpoints';
import { readTaskNavState, TaskNavState, TaskNotice, taskListPath } from '../taskListFilters';

/**
 * QZ-D13, QZ-D37, QZ-D41: a single task's review page at `/tasks/:taskId`. A draft is edited
 * through `TaskForm` and can be approved, saving any edits first, or rejected, discarding them;
 * an approved or rejected task is shown read-only through `TaskView` and can be reopened to a
 * draft. Approving or rejecting a draft moves on to the next draft of the list it was opened
 * from. Keyed on the task id, so moving on starts the next task with fresh state.
 */
export const TaskPage = () => {
  const { taskId } = useParams();
  return <TaskReview key={taskId} taskId={taskId} />;
};

const TaskReview = ({ taskId }: { taskId: string | undefined }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigateTo = useNavigate();
  const { from, notice } = readTaskNavState(location.state);

  const {
    data: task,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useGetTaskQuery(taskId ?? skipToken);
  const { data: tagData } = useListTagsQuery();
  const tags = tagData?.tags ?? [];
  const [updateTask, { isLoading: updating }] = useUpdateTaskMutation();
  const [changeStatus, { isLoading: changing }] = useChangeTaskStatusMutation();
  const [fetchFirstPage, { isFetching: findingNext }] = useLazyListTasksFirstPageQuery();

  const [dirty, setDirty] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  /** Set once a status change succeeded, so the refetched task does not flash before moving on. */
  const [movingOn, setMovingOn] = useState(false);
  // React Router's navigate still works after unmount, so a lookup that ends after the reviewer
  // left must not pull them back.
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );
  const [reason, setReason] = useState('');
  const [mutationError, setMutationError] = useState<ProblemCode | 'unknown' | undefined>(
    undefined
  );

  const busy = updating || changing || isFetching || findingNext;

  /**
   * Runs a mutation, clearing any previous mutation error first (QZ-D37). Resolves to the
   * mutation's result, or `undefined` once its error is shown.
   */
  const run = async <T,>(promise: { unwrap: () => Promise<T> }): Promise<T | undefined> => {
    setMutationError(undefined);
    try {
      return await promise.unwrap();
    } catch (thrown) {
      setMutationError(problemCode(thrown) ?? 'unknown');
      return undefined;
    }
  };

  /**
   * Approve saves the draft's edits first, if it has any, then approves against the `updatedAt`
   * the save returned. Two calls rather than a combined endpoint, so each keeps its own
   * optimistic-concurrency check (QZ-D41). A failed save approves nothing; a failed approve after
   * a good save leaves the edits saved on the draft, as two clicks would have.
   */
  const handleApprove = async (edits?: UpdateTaskRequest) => {
    if (!task) {
      return;
    }
    let expectedUpdatedAt = task.updatedAt;
    if (edits) {
      const saved = await run(updateTask({ taskId: task.taskId, request: edits }));
      if (!saved) {
        return;
      }
      expectedUpdatedAt = saved.updatedAt;
    }
    const approved = await run(
      changeStatus({
        taskId: task.taskId,
        request: { status: 'approved', expectedUpdatedAt },
      })
    );
    if (approved) {
      await moveOn(approved, 'approved');
    }
  };

  /**
   * After a draft is approved or rejected, opens the next draft of the list the reviewer came
   * from (the unfiltered drafts if none), with a notice linking back to the processed task. With
   * no drafts left, shows the approved tasks under the same filters.
   */
  const moveOn = async (processed: Task, outcome: TaskNotice['outcome']) => {
    setMovingOn(true);
    const drafts: TaskListFilters = { ...from, status: 'draft' };
    const nextNotice: TaskNotice = {
      outcome,
      taskId: processed.taskId,
      question: processed.translations[processed.originalLanguage]?.text ?? '',
    };
    const navigate = (to: string, state: TaskNavState) => {
      if (mounted.current) {
        navigateTo(to, { state });
      }
    };
    try {
      const page = await fetchFirstPage(drafts).unwrap();
      // The list's index is eventually consistent, so it may still hold the task just processed.
      const next = page.tasks.find((candidate) => candidate.taskId !== processed.taskId);
      if (next) {
        navigate(`/tasks/${next.taskId}`, { from: drafts, notice: nextNotice });
      } else {
        // A later page may still hold a draft; the drafts list will show it.
        const status = page.nextCursor ? 'draft' : 'approved';
        navigate(taskListPath({ ...drafts, status }), { notice: nextNotice });
      }
    } catch {
      // The status change itself succeeded, so a failed lookup lands on the drafts list.
      navigate(taskListPath(drafts), { notice: nextNotice });
    }
  };

  const handleReject = () => {
    setRejecting(true);
    setReason('');
  };

  const handleCancelReject = () => {
    setRejecting(false);
    setReason('');
  };

  const handleConfirmReject = async () => {
    if (!task) {
      return;
    }
    const trimmed = reason.trim();
    const rejected = await run(
      changeStatus({
        taskId: task.taskId,
        request: {
          status: 'rejected',
          expectedUpdatedAt: task.updatedAt,
          ...(trimmed ? { rejectionReason: trimmed } : {}),
        },
      })
    );
    if (rejected) {
      await moveOn(rejected, 'rejected');
    }
  };

  const handleReopen = async () => {
    if (!task) {
      return;
    }
    await run(
      changeStatus({
        taskId: task.taskId,
        request: { status: 'draft', expectedUpdatedAt: task.updatedAt },
      })
    );
  };

  const handleReload = () => {
    setMutationError(undefined);
    refetch();
  };

  const loadErrorCode = problemCode(error);
  const notFound =
    !task &&
    !!error &&
    (loadErrorCode === 'TaskNotFound' || (error as FetchBaseQueryError).status === 404);

  // Back to the list of the task's own status, under the filters it was opened with, if any.
  const backTo = taskListPath({ ...from, status: task?.status ?? 'draft' });
  const question = task?.translations[task.originalLanguage]?.text ?? '';

  const mutationAlert =
    mutationError &&
    (mutationError === 'TaskChanged' ? (
      <div className="alert alert-warning">
        <Icon name="rotate" />
        <div className="flex-grow-1">{t('task.page.changed')}</div>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleReload}>
          {t('task.page.reload')}
        </button>
      </div>
    ) : (
      <div className="alert alert-danger">
        <Icon name="circle-exclamation" />
        <div>{t(mutationError === 'unknown' ? 'error.unknown' : `error.${mutationError}`)}</div>
      </div>
    ));

  return (
    <main className="qz-main pb-0">
      <div className="qz-container">
        <Link className="qz-back" to={task ? backTo : '/tasks'}>
          <Icon name="arrow-left" /> {t(task ? `task.page.back.${task.status}` : 'nav.tasks')}
        </Link>

        <TaskOutcomeNotice key={location.key} notice={notice} />

        {movingOn && <p className="qz-muted">{t('task.page.movingOn')}</p>}

        {isLoading && <p className="qz-muted">{t('task.page.loading')}</p>}

        {!task && !isLoading && error && (
          <div className="alert alert-danger">
            <Icon name="circle-exclamation" />
            <div>
              {t(
                notFound
                  ? 'task.page.notFound'
                  : loadErrorCode
                    ? `error.${loadErrorCode}`
                    : 'error.unknown'
              )}
            </div>
          </div>
        )}

        {task && !movingOn && task.status === 'draft' && (
          <>
            <div className="d-flex align-items-center gap-2 mb-3">
              <h1 className="visually-hidden">{t('task.page.reviewHeading')}</h1>
              <TaskStatusPill status={task.status} />
              <LanguageCode language={task.originalLanguage} />
              <span className="qz-small qz-muted">
                {t('task.page.original')} · {t('task.page.updated')}{' '}
                <RelativeTime iso={task.updatedAt} />
              </span>
              {task.jobId && <TaskJobLink jobId={task.jobId} />}
            </div>
            {mutationAlert}
            <TaskForm
              key={task.updatedAt}
              task={task}
              tags={tags}
              onDirtyChange={setDirty}
              actions={(form) => (
                <>
                  <span className="vr mx-1" />
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    disabled={busy || rejecting}
                    onClick={handleReject}
                  >
                    {t('task.page.reject')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={busy || (form.dirty && !form.valid)}
                    title={form.dirty && !form.valid ? t('task.form.fixFields') : undefined}
                    onClick={() => handleApprove(form.dirty ? form.request : undefined)}
                  >
                    <Icon name="check" /> {t('task.page.approve')}
                  </button>
                </>
              )}
            >
              {rejecting && (
                <div className="qz-card mt-3 border-danger-subtle">
                  <div className="qz-card-body">
                    <div className="d-flex gap-2 align-items-baseline">
                      <label htmlFor="task-rejectionReason" className="form-label">
                        {t('task.field.rejectionReason')}
                      </label>
                      <span className="qz-small qz-muted">{t('task.page.optional')}</span>
                    </div>
                    <textarea
                      id="task-rejectionReason"
                      className="form-control"
                      rows={2}
                      maxLength={500}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                    <div className="form-text">{t('task.page.rejectHint')}</div>
                    {dirty && (
                      <div className="form-text text-danger">{t('task.page.rejectDiscards')}</div>
                    )}
                    <div className="mt-3 d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-danger"
                        disabled={busy}
                        onClick={handleConfirmReject}
                      >
                        {t('task.page.confirmReject')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleCancelReject}
                      >
                        {t('task.page.cancel')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </TaskForm>
          </>
        )}

        {task && !movingOn && task.status !== 'draft' && (
          <div className="pb-5">
            <div className="qz-page-header align-items-start">
              <div>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <TaskStatusPill status={task.status} />
                  <LanguageCode language={task.originalLanguage} />
                  <span className="qz-small qz-muted">
                    {t('task.page.original')} · {t('task.page.updated')}{' '}
                    <RelativeTime iso={task.updatedAt} />
                  </span>
                  {task.jobId && <TaskJobLink jobId={task.jobId} />}
                </div>
                <h1 className="qz-question" style={{ maxWidth: '46rem' }}>
                  {question}
                </h1>
              </div>
              <div className="text-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={busy}
                  onClick={handleReopen}
                >
                  <Icon name="rotate-left" /> {t('task.page.reopen')}
                </button>
                <div className="form-text" style={{ maxWidth: '16rem' }}>
                  {t('task.page.reopenHint')}
                </div>
              </div>
            </div>
            {mutationAlert}
            {task.status === 'rejected' && (
              <div className="alert alert-danger">
                <Icon name="circle-xmark" />
                <div>
                  <b>{t('task.field.rejectionReason')}:</b>{' '}
                  {task.rejectionReason ?? t('task.list.noReason')}
                </div>
              </div>
            )}
            <TaskView task={task} />
          </div>
        )}
      </div>
    </main>
  );
};

export default TaskPage;
